import * as React from "react";
import { broadcast, subscribe, useChannel } from "../hooks/useChannel";

export const NavChannels = {
	NAVIGATE_REQUEST: "nav/request",
	NAVIGATE_TO: "nav/to"
}

export const url = {
    host: "",
    href: "",
    path: "",
    qry: "",
    params: {} as any,
    pathParts: [] as string[],

	getPathPart: function(index: number): string {
		if (index >= 0 && index < url.pathParts.length) {
			return url.pathParts[index];
		} else {
			return "";
		}
	},
	
	get: function(paramName: string): string {
		if (typeof url.params[paramName] === "undefined") {
			return "";
		} else {
			return url.params[paramName];
		}
	},

};

// initial the url object
parse(window.location);

subscribe(NavChannels.NAVIGATE_TO, (msg: any) => {
	parse(window.location);
});

function parse(location: Location) {
	url.host = location.host;
	url.href = location.href;

	url.path = url.href.substring(location.origin.length);
	
	// is there a bookmark?
	const iHash = url.path.indexOf("#");
	if (iHash >= 0) {
		url.path = url.path.substring(0, iHash);
	}

	url.pathParts = url.path.split("/");

	// decode the path parts
	if (url.pathParts && url.pathParts.length > 0) {
		for(let i=0;i<url.pathParts.length;i++) {
			url.pathParts[i] = decodeURIComponent(url.pathParts[i]);
		}
	}

	var i = url.href.indexOf("?");
	if (i >= 0) {
		url.qry = location.href.substring(i+1);

		const parts = url.qry.split("&");
		for(i=0;i<parts.length;i++) {
			var tmp = parts[i].split("=");
			if (tmp.length === 2) {
				url.params[tmp[0]] = tmp[1];
			}
		}
	}
}

export function getQueryStringValue(name: string, url?: string) {
    if (!url) {
        url = window.location.href;
    }
    name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
        return null;
    }
    if (!results[2]) {
        return '';
    }
    const result = decodeURIComponent(results[2].replace(/\+/g, ' '));
    return result;
}


export interface Match {
    currentPathIndex: number;
}

export function Route(props: {
	children?: any;

    match: string,

    render: (props: any) => any,

    exact?: boolean,

    currentMatch?: Match,
}) {

	const [, setState] = React.useState({});

	useChannel(NavChannels.NAVIGATE_TO, React.useCallback(() => {
		// refresh
		setState({});
	}, [setState]));

	const {
		// children,
		render,
		match,
		exact,
		currentMatch,
	} = props;
	
	var isMatch = true;

	const extraProps: any = {
		match: {
			currentPathIndex: currentMatch ? currentMatch.currentPathIndex : 1,
		}
	};

	const parts = match.split("/");
	if (exact && parts.length !== url.pathParts.length) {
		isMatch = false;

	} else {
		for(var i=1;i<parts.length;i++) {
			const part = parts[i];
			if (part.startsWith(":")) {
				//param
				const paramName = part.substring(1);
				extraProps[paramName] = url.getPathPart(extraProps.match.currentPathIndex);
			} else {
				const comp = url.getPathPart(extraProps.match.currentPathIndex);
				if (comp === part) {
					// is matching so far
				} else {
					// is this the last part?
					if (extraProps.match.currentPathIndex === parts.length-1) {
						if (part === "" && comp.startsWith("?")) {
							// bookmark is only diff, so treat as same
							isMatch = true;
							break;
						}
					}
					
					// do we not care about exact match, and got to end matching everything we are aware should exist?
					if (isMatch && !exact && i === (parts.length-1) && part === "") {
						isMatch = true;
						break;
					}

					// not a match
					isMatch = false;
					break;
				}
			}

			extraProps.match.currentPathIndex++;
		}
	}

	if (!isMatch) {
		return null;
	}

	return (
		render(extraProps)
	);	
}

function canNavigate(state: any): string {
    var wasCancelled = false;
    var cancelMessage = null;

    // first send off request to see if everyone happy with nav request
    state.cancel = () => {
        wasCancelled = true;
    };
    broadcast(NavChannels.NAVIGATE_REQUEST, state);

    // remove the event - causes cloning issues in pushState otherwise
    state.cancel = undefined;

    if (wasCancelled) {
        // don't continue
        return (cancelMessage === null) ? "" : cancelMessage;
    }

    return "";
}

export function back() {
    window.history.back();
}

// NOTE: required for when have promises and various state happening 
// such as plumbing for modals which may have to complete before navigating away
// to avoid attempting to update components which have been flushed
export function goSoon(url: string, options?: any) {
    setTimeout(() => {
        go(url, options);
    }, 50);
}

export function go(url: string, options?: any) {
    const state = {
        url,
    };

	// check for relative path
    if (url.startsWith("./")) {
        state.url = window.location.pathname + state.url.substr(2);
    }
    if (state.url.indexOf("?") < 0 && !state.url.endsWith("/")) {
        state.url += "/";
    }

    if (options && options.force)  {
        // if forcing nav away then don't both attempting to get everyone's approval
    } else {
        // check that everyone is happy
        const cancelMsg = canNavigate(state);
        if (cancelMsg) {
            return;
        }
    }

    // perform the nav
    window.history.pushState(state, "", state.url);
    broadcast(NavChannels.NAVIGATE_TO, state);
}

// init history
window.history.replaceState(null, "", "");

window.addEventListener("beforeunload", (e) => {
    const state = {
        url: "",
    };

    var cancelMsg = canNavigate(state);
    if (cancelMsg !== null) {
        if (cancelMsg.length <= 0) {
            cancelMsg = "You have unsaved changes you will lose if you leave.";
        }
        return cancelMsg;
    }

    return undefined;
});

function handleStateChange(e: any) {
    const state = {
        url: "",
    };

    var cancelMsg = canNavigate(state);
    if (cancelMsg !== null) {
        e.preventDefault();
        return false;
    }

    broadcast(NavChannels.NAVIGATE_TO);

    return true;
}

(function(history) {
	const originalPushState = history.pushState;
	history.pushState = function(state, title, url) {
	  originalPushState.apply(history, arguments as any);
	  window.dispatchEvent(new Event('pushstate'));
	};
  
	const originalReplaceState = history.replaceState;
	history.replaceState = function(state, title, url) {
	  originalReplaceState.apply(history, arguments as any);
	  window.dispatchEvent(new Event('replacestate'));
	};
  })(window.history);

window.addEventListener("popstate", (e) => handleStateChange);
window.addEventListener("pushstate", (e) => handleStateChange);
window.addEventListener("replacestate", (e) => handleStateChange);



